/**
 * NovedadesPanel — compact "what's changed since your last visit" banner.
 *
 * V1 design (PR #3 scope, restraint pass):
 *   - DEFAULT STATE: a single-line banner with bell + count + "Ver detalles".
 *     On mobile: shorter copy ("3 novedades" + "Ver").
 *   - EXPANDED: max 2 items, then "Ver todas" reveals the rest. Items are
 *     dense rows (no large cards). "Marcar como leído" per item +
 *     "Marcar todo como leído" at the top.
 *   - EMPTY: render nothing — the construction progress bar should dominate.
 *   - DEVICE LIMITATION HINT: shown only when expanded, as a tiny muted
 *     footer. Investors don't need this to be visible by default.
 *
 * No backend collection. Items derived from existing /documents, /payments,
 * /villas/{id}.progress endpoints already fetched by the dashboard.
 * Read state lives in localStorage under `investor_novedades_<user_id>_read`
 * (Set of item ids). Device-specific by design — backend persistence is a
 * future PR if the feature sticks.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Bell, CaretDown, CaretUp, FileText, Receipt, Buildings, Check, Files, X,
} from "@phosphor-icons/react";

const TERRACOTTA = "#9c5236";
const WINDOW_DAYS = 30;
const MAX_ITEMS = 12;
const DEFAULT_EXPANDED_ITEMS = 2;

function safeParseDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}
function withinWindow(iso) {
  const d = safeParseDate(iso);
  if (!d) return false;
  return Date.now() - d.getTime() < WINDOW_DAYS * 86400000;
}
function fmtDate(iso, locale = "es-ES") {
  const d = safeParseDate(iso);
  if (!d) return "";
  return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
}
function lsKey(userId, suffix) {
  return `investor_novedades_${userId || "anon"}_${suffix}`;
}
function readReadSet(userId) {
  try {
    const raw = localStorage.getItem(lsKey(userId, "read"));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}
function writeReadSet(userId, set) {
  try {
    localStorage.setItem(lsKey(userId, "read"), JSON.stringify(Array.from(set)));
  } catch { /* noop */ }
}

function deriveItems({ documents = [], payments = [], progress = [], locale = "es-ES", t }) {
  const items = [];

  for (const d of documents) {
    if (!d || !d.doc_id) continue;
    if (withinWindow(d.created_at)) {
      items.push({
        id: `doc_new_${d.doc_id}`,
        type: "document_new",
        title: d.title || t("documents.cat.other"),
        message: d.doc_type || "",
        date: d.created_at,
        entity: { kind: "document", id: d.doc_id },
      });
    }
    if (d.updated_at && d.updated_at !== d.created_at && withinWindow(d.updated_at)) {
      items.push({
        id: `doc_upd_${d.doc_id}_${d.updated_at}`,
        type: "document_upd",
        title: d.title || t("documents.cat.other"),
        message: d.doc_type || "",
        date: d.updated_at,
        entity: { kind: "document", id: d.doc_id },
      });
    }
  }
  for (const p of progress) {
    if (!p || !p.progress_id) continue;
    if (withinWindow(p.created_at)) {
      items.push({
        id: `prog_${p.progress_id}`,
        type: "progress_new",
        title: p.description || p.phase || "—",
        message: typeof p.percentage === "number" ? `${p.percentage}%` : "",
        date: p.created_at,
        entity: { kind: "progress", id: p.progress_id },
      });
    }
  }
  for (const pay of payments) {
    if (!pay || !pay.payment_id) continue;
    if (withinWindow(pay.created_at)) {
      items.push({
        id: `pay_new_${pay.payment_id}`,
        type: "payment_new",
        title: pay.phase || "—",
        message: pay.due_date ? fmtDate(pay.due_date, locale) : "",
        date: pay.created_at,
        entity: { kind: "payment", id: pay.payment_id },
      });
    }
    if (pay.updated_at && pay.updated_at !== pay.created_at && withinWindow(pay.updated_at)) {
      items.push({
        id: `pay_upd_${pay.payment_id}_${pay.updated_at}`,
        type: "payment_upd",
        title: pay.phase || "—",
        message: pay.due_date ? fmtDate(pay.due_date, locale) : "",
        date: pay.updated_at,
        entity: { kind: "payment", id: pay.payment_id },
      });
    }
  }

  items.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return items.slice(0, MAX_ITEMS);
}

const TYPE_META = {
  document_new: { Icon: FileText,  tKey: "novedades.type.document_new" },
  document_upd: { Icon: Files,     tKey: "novedades.type.document_upd" },
  progress_new: { Icon: Buildings, tKey: "novedades.type.progress_new" },
  payment_new:  { Icon: Receipt,   tKey: "novedades.type.payment_new"  },
  payment_upd:  { Icon: Receipt,   tKey: "novedades.type.payment_upd"  },
};

export default function NovedadesPanel({
  userId,
  documents = [],
  payments = [],
  progress = [],
  t,
  locale = "es-ES",
  onOpenEntity,
}) {
  const [readSet, setReadSet] = useState(() => readReadSet(userId));
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => { setReadSet(readReadSet(userId)); }, [userId]);

  const allItems = useMemo(
    () => deriveItems({ documents, payments, progress, locale, t }),
    [documents, payments, progress, locale, t]
  );

  const unreadCount = useMemo(
    () => allItems.filter(it => !readSet.has(it.id)).length,
    [allItems, readSet]
  );

  // EMPTY STATE: render nothing. The construction progress bar dominates the
  // visual hierarchy. We intentionally drop the empty-state card per UX
  // feedback ("Prefer hiding it if it creates too much visual noise").
  if (allItems.length === 0) return null;

  const markRead = (id) => {
    const next = new Set(readSet);
    next.add(id);
    setReadSet(next);
    writeReadSet(userId, next);
  };
  const markAllRead = () => {
    const next = new Set(readSet);
    allItems.forEach(it => next.add(it.id));
    setReadSet(next);
    writeReadSet(userId, next);
  };

  const totalCount = allItems.length;
  // Banner count = unread if any, otherwise total (so the user sees the
  // module's relevance even after marking everything read in this session).
  const displayCount = unreadCount > 0 ? unreadCount : totalCount;
  const bannerLong = displayCount === 1
    ? t("novedades.banner_one")
    : t("novedades.banner_many", { count: displayCount });
  const bannerShort = displayCount === 1
    ? t("novedades.badge_one")
    : t("novedades.badge_many", { count: displayCount });

  // --- COMPACT BANNER (default) ---
  if (!expanded) {
    return (
      <div
        className="mb-4 flex items-center gap-2 rounded-md border px-3 py-2 text-sm bg-[#fbf3ee] dark:bg-stone-900 border-[#e4cdbf] dark:border-stone-700"
        data-testid="novedades-panel"
        data-state="collapsed"
      >
        <Bell size={14} weight="regular" style={{ color: TERRACOTTA }} className="shrink-0" />
        {/* Long copy on sm+, short on mobile. */}
        <span className="hidden sm:inline text-stone-700 dark:text-stone-200 truncate">{bannerLong}</span>
        <span className="sm:hidden text-stone-700 dark:text-stone-200 truncate">{bannerShort}</span>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="novedades-expand"
          className="ml-auto shrink-0 text-xs font-medium hover:underline"
          style={{ color: TERRACOTTA }}
        >
          <span className="hidden sm:inline">{t("novedades.see_details")}</span>
          <span className="sm:hidden">{t("novedades.see_short")}</span>
        </button>
      </div>
    );
  }

  // --- EXPANDED PANEL ---
  const shown = showAll ? allItems : allItems.slice(0, DEFAULT_EXPANDED_ITEMS);
  const hasMore = allItems.length > DEFAULT_EXPANDED_ITEMS;

  return (
    <div
      className="mb-4 rounded-md border px-3 py-2 bg-[#fbf3ee] dark:bg-stone-900 border-[#e4cdbf] dark:border-stone-700"
      data-testid="novedades-panel"
      data-state="expanded"
    >
      {/* Header — single row, dense */}
      <div className="flex items-center gap-2 mb-1.5">
        <Bell size={14} weight="regular" style={{ color: TERRACOTTA }} className="shrink-0" />
        <span className="text-sm font-medium text-stone-800 dark:text-stone-100 truncate">
          {bannerLong}
        </span>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            data-testid="novedades-mark-all-read"
            className="ml-auto shrink-0 text-[11px] text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-50 hover:underline"
          >
            {t("novedades.mark_all_read")}
          </button>
        )}
        <button
          type="button"
          onClick={() => { setExpanded(false); setShowAll(false); }}
          aria-label="Collapse"
          data-testid="novedades-collapse"
          className="shrink-0 ml-1 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-50"
        >
          <X size={12} weight="bold" />
        </button>
      </div>

      <ul className="space-y-1">
        {shown.map(it => {
          const meta = TYPE_META[it.type] || { Icon: Bell, tKey: "novedades.title" };
          const Icon = meta.Icon;
          const unread = !readSet.has(it.id);
          return (
            <li
              key={it.id}
              data-testid={`novedades-item-${it.type}`}
              data-unread={unread ? "true" : "false"}
              className="flex items-center gap-2 text-xs py-1"
            >
              <Icon size={12} weight="regular" className={unread ? "" : "text-stone-400 dark:text-stone-500"} style={unread ? { color: TERRACOTTA } : undefined} />
              <span className="text-stone-500 dark:text-stone-400 shrink-0 hidden sm:inline">{t(meta.tKey)}:</span>
              <button
                type="button"
                onClick={() => onOpenEntity && onOpenEntity(it.entity)}
                className={`text-left truncate hover:underline ${unread ? "text-stone-800 dark:text-stone-100 font-medium" : "text-stone-600 dark:text-stone-300"}`}
                title={it.title}
              >
                {it.title}
              </button>
              <span className="text-[10px] text-stone-400 dark:text-stone-500 shrink-0 ml-auto">
                {fmtDate(it.date, locale)}
              </span>
              {unread && (
                <button
                  type="button"
                  onClick={() => markRead(it.id)}
                  data-testid={`novedades-mark-read-${it.id}`}
                  aria-label={t("novedades.mark_read")}
                  title={t("novedades.mark_read")}
                  className="shrink-0 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-50"
                >
                  <Check size={11} weight="bold" />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll(v => !v)}
          data-testid="novedades-toggle-more"
          className="mt-1 inline-flex items-center gap-1 text-[11px] text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-50"
        >
          {showAll
            ? (<><CaretUp size={10} weight="bold" /> {t("novedades.show_less")}</>)
            : (<><CaretDown size={10} weight="bold" /> {t("novedades.show_more")}</>)}
        </button>
      )}

      <div className="text-[10px] text-stone-400 dark:text-stone-500 mt-1.5 italic">
        {t("novedades.device_only")}
      </div>
    </div>
  );
}
